import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSessionToken } from '@/auth/session';

export const OVERVIEW_DEADLINE_MS = 9000;
export const OVERVIEW_REFRESH_MS = 300000;
const definitions = {
  news: { path: '/overview/news', label: 'industry headlines' },
  movers: { path: '/overview/movers', label: 'selected stock quotes' },
  filings: { path: '/sec/radar/summary?limit=8', label: 'SEC filings' },
  catalysts: { path: '/catalysts?limit=8', label: 'upcoming catalysts' },
  pins: { path: '/overview/pins', label: 'your companies', private: true, field: 'companies' },
  watches: { path: '/catalysts/watches', label: 'your catalyst alerts', private: true, field: 'alerts' },
  changes: { path: '/overview/changes', label: 'saved updates', private: true },
  exports: { path: '/export/my-exports?limit=6', label: 'your saved exports', private: true, field: 'exports' },
};
const blank = (state = 'loading') => ({ state, data: [], refreshing: false, error: null });
const DEFAULT_KEYS = ['news', 'movers', 'filings', 'catalysts', 'pins', 'watches', 'exports'];
const initialFeeds = (keys = DEFAULT_KEYS) => Object.fromEntries(keys.map(key => [key, blank()]));

// Promise deadline remains effective even if the browser/provider ignores abort.
export async function requestOverviewFeed(url, { token, signal, method = 'GET', body, deadlineMs = OVERVIEW_DEADLINE_MS } = {}) {
  const controller = new AbortController();
  let timer;
  let stop;
  const abort = () => { controller.abort(); stop?.(Object.assign(new Error('Request cancelled'), { name: 'AbortError' })); };
  try {
    if (signal?.aborted) throw Object.assign(new Error('Request cancelled'), { name: 'AbortError' });
    const cancelled = new Promise((_, reject) => { stop = reject; });
    signal?.addEventListener('abort', abort, { once: true });
    const deadline = new Promise((_, reject) => {
      timer = setTimeout(() => { reject(Object.assign(new Error('This section is taking longer than expected. Retry to check again.'), { code: 'TIMEOUT' })); controller.abort(); }, deadlineMs);
    });
    const response = await Promise.race([fetch(url, { method, signal: controller.signal, headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) }), deadline, cancelled]);
    if (!response.ok) throw Object.assign(new Error(response.status === 401 ? 'Sign in again to view your saved items.' : 'This section could not be loaded. Retry to check again.'), { code: [401, 403].includes(response.status) ? 'AUTH_REQUIRED' : 'REQUEST_FAILED', status: response.status });
    // Body decoding also shares the same deadline.
    return await Promise.race([response.json(), deadline, cancelled]);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}

function normalized(payload, definition) {
  const data = payload?.[definition.field || 'data'];
  if (!Array.isArray(data)) throw Object.assign(new Error('This section returned an unexpected response. Retry to check again.'), { code: 'INVALID_RESPONSE' });
  return { ...payload, data, state: payload.state || (data.length ? 'ready' : 'empty'), refreshing: false, error: null };
}

export function useOverviewFeeds({ api, userId, orgId, enabledKeys = DEFAULT_KEYS }) {
  const keySignature = enabledKeys.filter(key => definitions[key]).join(',');
  const keys = useMemo(() => keySignature.split(',').filter(Boolean), [keySignature]);
  const token = useSessionToken();
  const scope = `${userId || ''}:${orgId || ''}:${token || ''}`;
  const [feeds, setFeeds] = useState(() => initialFeeds(keys));
  const active = useRef(new Map());
  const sequence = useRef(0);
  const alive = useRef(true);
  const currentScope = useRef(scope);
  currentScope.current = scope;

  const load = useCallback(async (key, background = false) => {
    const definition = definitions[key];
    if (currentScope.current !== scope || !definition || (background && document.visibilityState === 'hidden')) return;
    if (background && active.current.has(key)) return;
    active.current.get(key)?.controller.abort();
    const id = ++sequence.current;
    const controller = new AbortController();
    active.current.set(key, { id, controller });
    if (definition.private && (!token || !userId)) {
      active.current.delete(key);
      setFeeds(previous => ({ ...previous, [key]: { ...blank('anonymous'), scope } }));
      return;
    }
    setFeeds(previous => ({ ...previous, [key]: {
      ...(definition.private && previous[key]?.scope !== scope ? blank() : previous[key]),
      scope, refreshing: true, error: null,
    } }));
    const started = performance.now();
    let state = 'error';
    try {
      const payload = await requestOverviewFeed(`${api}${definition.path}`, { token: definition.private ? token : null, signal: controller.signal });
      const result = normalized(payload, definition);
      state = result.state;
      if (alive.current && currentScope.current === scope && active.current.get(key)?.id === id) {
        setFeeds(previous => ({ ...previous, [key]: { ...result, scope } }));
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (alive.current && currentScope.current === scope && active.current.get(key)?.id === id) {
        setFeeds(previous => ({ ...previous, [key]: {
          ...previous[key], data: error.code === 'AUTH_REQUIRED' ? [] : previous[key].data, state: previous[key].data.length && error.code !== 'AUTH_REQUIRED' ? 'stale' : 'error', refreshing: false,
          error: { code: error.code || 'REQUEST_FAILED', message: error.message }, scope,
        } }));
      }
    } finally {
      if (active.current.get(key)?.id === id) active.current.delete(key);
      // Local diagnostic only: never includes session, account, query, or provider payload.
      if (!controller.signal.aborted) window.dispatchEvent(new CustomEvent('bds-overview-widget', { detail: { widget: key, duration_ms: Math.round(performance.now() - started), state } }));
    }
  }, [api, userId, token, scope]);

  useEffect(() => {
    alive.current = true;
    const requests = active.current;
    requests.forEach(({ controller }) => controller.abort());
    requests.clear();
    setFeeds(initialFeeds(keys));
    keys.forEach(key => { void load(key); });
    const interval = setInterval(() => keys.forEach(key => { void load(key, true); }), OVERVIEW_REFRESH_MS);
    const visible = () => { if (document.visibilityState === 'visible') keys.forEach(key => { void load(key, true); }); };
    document.addEventListener('visibilitychange', visible);
    return () => {
      alive.current = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', visible);
      requests.forEach(({ controller }) => controller.abort());
      requests.clear();
    };
  }, [load, keys]);

  // Mask private records during the render before an account-switch effect runs.
  const visibleFeeds = Object.fromEntries(Object.entries(feeds).map(([key, feed]) => [key,
    definitions[key].private && feed.scope !== scope ? blank(token && userId ? 'loading' : 'anonymous') : feed]));
  return { feeds: visibleFeeds, retry: key => load(key), labels: definitions };
}
